// All comments in English only
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from "recharts";

interface Point {
  usage_month: string;             // date string
  ur_cap?: number | null;          // 0..1
  ur_ewma_12m?: number | null;     // 0..1
  slope_4m?: number | null;
  risk_score_final?: number | null;
}
interface Props {
  product: string;
  series: Point[];
}

export default function ProductRiskTrendChart({ product, series }: Props) {
  // Preprocess data: convert to % and compute the gap for contrast
  const rows = (series || []).map((p) => {
    const ur = p.ur_cap == null ? null : Math.round((p.ur_cap as number) * 100);
    const urEwma =
      p.ur_ewma_12m == null ? null : Math.round((p.ur_ewma_12m as number) * 100);
    const gap =
      ur == null || urEwma == null ? 0 : Math.abs(ur - urEwma); // absolute gap for easy comparison
    return {
      month: (p.usage_month || "").slice(0, 7),
      ur,
      urEwma,
      gap,
      risk: p.risk_score_final ?? null,
    };
  });

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>{product} — Risk Metrics Trend</CardTitle>
      </CardHeader>
      <CardContent className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows} margin={{ left: 8, right: 8, top: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" minTickGap={24} />
            {/* Left axis for percentages */}
            <YAxis yAxisId="left" domain={[0, 100]} />
            {/* Right axis for risk score */}
            <YAxis yAxisId="right" orientation="right" domain={[0, "auto"]} />
            <Tooltip
              // Custom formatter to show percentage for UR values
              formatter={(value: any, name: string) => {
                if (name === "UR Cap %" || name === "UR EWMA %" || name === "UR Gap")
                  return [`${value}%`, name];
                return [value, name];
              }}
            />
            <Legend />
            {/* Optional visual guide at 100% */}
            <ReferenceLine y={100} yAxisId="left" stroke="#e5e7eb" strokeDasharray="4 4" />

            {/* NEW: Gap bar to emphasize contrast between UR and UR EWMA */}
            <Bar
              yAxisId="left"
              dataKey="gap"
              name="UR Gap"
              barSize={18}
              fill="#f59e0b"      // amber-500
              opacity={0.75}
              radius={[4, 4, 0, 0]}
            />

            {/* UR Cap (green solid) */}
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="ur"
              name="UR Cap %"
              dot={false}
              stroke="#10b981"    // emerald-500
              strokeWidth={2.5}
              activeDot={{ r: 5 }}
            />

            {/* UR EWMA (teal dashed) */}
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="urEwma"
              name="UR EWMA %"
              dot={false}
              stroke="#06b6d4"    // cyan-500
              strokeDasharray="6 4"
              strokeWidth={2.5}
              activeDot={{ r: 5 }}
            />

            {/* Risk Score (orange line on right axis) */}
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="risk"
              name="Risk Score"
              dot={false}
              stroke="#f97316"    // orange-500
              strokeWidth={2.5}
              activeDot={{ r: 5 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
